import React from "react";
import {
  PhoneCall,
  PhoneIncoming,
  PhoneOutgoing,
  Mail,
  MessageSquare,
  ArrowRightLeft,
  Sparkles,
  Bot,
  CheckCircle2,
  Upload,
  FileText,
  PhoneMissed,
  Voicemail,
} from "lucide-react";
import { tierConfig } from "./AIScoreBadge";

/**
 * Activity timeline unifiée — fusionne :
 *   - activities[] de l'entité
 *   - timeline[] (push hooks Aircall + status changes auto)
 *   - aircall_call_logs (depuis l'overview API)
 *
 * Utilise le tableau `timeline` retourné par /overview directement.
 *
 * Props :
 *   timeline : array d'items normalisés { kind, title, content, at, by, source, meta }
 */
const TYPE_META = {
  note: { icon: MessageSquare, color: "#3b82f6", label: "Note" },
  call: { icon: PhoneCall, color: "#22c55e", label: "Appel" },
  email: { icon: Mail, color: "#a855f7", label: "Email" },
  sms: { icon: MessageSquare, color: "#06b6d4", label: "SMS" },
  status_change: { icon: ArrowRightLeft, color: "#f59e0b", label: "Statut" },
  conversion: { icon: CheckCircle2, color: "#22c55e", label: "Conversion" },
  import: { icon: Upload, color: "#64748b", label: "Import" },
  ai_evaluation: { icon: Bot, color: "#f7c935", label: "Évaluation IA" },
  auto_qualified: { icon: Sparkles, color: "#f7c935", label: "Auto-qualifié" },
  devis: { icon: FileText, color: "#f97316", label: "Devis" },
  voicemail: { icon: Voicemail, color: "#a855f7", label: "Messagerie" },
  missed: { icon: PhoneMissed, color: "#ef4444", label: "Appel manqué" },
};

const formatRelative = (iso) => {
  if (!iso) return "—";
  try {
    const dt = new Date(iso);
    const diff = (Date.now() - dt.getTime()) / 1000;
    if (diff < 60) return "à l'instant";
    if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
    if (diff < 86400 * 7) return `il y a ${Math.floor(diff / 86400)} j`;
    return dt.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "2-digit" });
  } catch {
    return iso.slice(0, 16);
  }
};

const formatFull = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

const TimelineItem = ({ item, last }) => {
  const meta = TYPE_META[item.kind] || TYPE_META.note;
  const Icon = meta.icon;
  const isCall = item.kind === "call";
  const isAi = item.kind === "ai_evaluation" || item.source === "ai" || item.source === "ai_aircall";
  const direction = item.meta?.direction;
  const duration = item.meta?.duration;
  const recording = item.meta?.recording_url;
  const tier = item.meta?.tier;

  return (
    <li className="relative pl-10 pb-6" data-testid={`tl-item-${item.kind}`}>
      {/* Vertical bar */}
      {!last && (
        <span
          className="absolute left-3.5 top-9 bottom-0 w-px bg-white/10"
          aria-hidden
        />
      )}
      {/* Icon dot */}
      <span
        className="absolute left-0 top-1 w-7 h-7 rounded-full flex items-center justify-center ring-4 ring-[#0b0811]"
        style={{ backgroundColor: `${meta.color}22`, color: meta.color }}
      >
        {isCall && direction === "inbound" ? (
          <PhoneIncoming className="w-3.5 h-3.5" />
        ) : isCall && direction === "outbound" ? (
          <PhoneOutgoing className="w-3.5 h-3.5" />
        ) : (
          <Icon className="w-3.5 h-3.5" />
        )}
      </span>

      <div className="bg-[#0b0811] dark:bg-[#0b0811] rounded-xl border border-white/5 p-3 hover:border-white/10 transition-colors">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className="text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wide"
              style={{ backgroundColor: `${meta.color}22`, color: meta.color }}
            >
              {meta.label}
            </span>
            {isCall && duration ? (
              <span className="text-[10px] text-slate-500">{duration}s</span>
            ) : null}
            {isAi && tier && (
              <span
                className="text-[10px] px-2 py-0.5 rounded-full font-semibold"
                style={{ backgroundColor: `${tierConfig(tier).text.replace('text-', '')}22` }}
              >
                {tierConfig(tier).label}
              </span>
            )}
            {item.by && <span className="text-[10px] text-slate-500">par {item.by}</span>}
          </div>
          <span
            className="text-[10px] text-slate-500"
            title={formatFull(item.at)}
          >
            {formatRelative(item.at)}
          </span>
        </div>

        {item.title && item.title !== meta.label && (
          <div className="mt-1.5 text-sm font-medium text-white leading-snug">
            {item.title}
          </div>
        )}

        {item.content && (
          <div className="mt-1 text-sm text-slate-300 whitespace-pre-wrap leading-relaxed">
            {item.content}
          </div>
        )}

        {/* Aircall extra meta */}
        {isCall && recording && (
          <a
            href={recording}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 mt-2 text-[11px] text-blue-400 hover:underline"
            data-testid="tl-recording-link"
          >
            ▶ Écouter l'enregistrement
          </a>
        )}
      </div>
    </li>
  );
};

export const EntityActivityTimeline = ({ timeline = [], emptyLabel = "Aucune activité enregistrée pour le moment.", testId = "entity-timeline" }) => {
  if (!timeline.length) {
    return (
      <div className="text-center py-10 text-sm text-slate-500" data-testid={`${testId}-empty`}>
        {emptyLabel}
      </div>
    );
  }
  return (
    <ol className="relative" data-testid={testId}>
      {timeline.map((it, i) => (
        <TimelineItem key={i} item={it} last={i === timeline.length - 1} />
      ))}
    </ol>
  );
};

export default EntityActivityTimeline;
